import getConfig from "next/config";
import { addDays } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { print, stripIgnoredCharacters } from "graphql";
import gql from "graphql-tag";
import type { DocumentNode } from "graphql/language/ast";
import { radioProfile } from "@constants/consts";
import { TIMEZONE } from "@constants/date";
import { Schedule, Station } from "./dataModels";

const {
  publicRuntimeConfig: { audioApiEndpoint, audioXApiKey },
} = getConfig();

const RadioScheduleQuery = gql`
  query RadioScheduleQuery($stationSlug: String, $startDate: AWSDateTime, $endDate: AWSDateTime) {
    station(slug: $stationSlug) {
      schedules(broadcastStartDate: $startDate, broadcastEndDate: $endDate) {
        broadcastStartDate
        broadcastEndDate
        id
        show {
          title
          slug
          images {
            uri
            type
            imageType
            variant {
              slug
            }
          }
        }
      }
    }
  }
`;

const getStation = gql`
  query GetStation($slug: String!) {
    station(slug: $slug) {
      id
      type
      title
      shortTitle
      slug
      media {
        type
        uri
        source
      }
      images {
        uri
        type
        description
        imageType
      }
    }
  }
`;

export default async function fetchAudioData(
  query: DocumentNode,
  variables: {
    stationSlug?: string;
    startDate?: Date;
    endDate?: Date;
    slug?: string;
  }
): Promise<{ data: { station: UniversalApiStation } | null }> {
  const queryParams = new URLSearchParams({
    query: stripIgnoredCharacters(print(query)),
    variables: JSON.stringify(variables),
  });

  const response = await fetch(`${audioApiEndpoint}?${queryParams.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": audioXApiKey,
      Profile: radioProfile,
    },
  });
  const { data, errors } = await response.json();

  if (errors?.length) {
    return { data: null };
  }

  return { data };
}

export async function fetchRadioSchedule(stationSlug?: string): Promise<Schedule[]> {
  if (!stationSlug) return [];

  const startDate = utcToZonedTime(new Date(), TIMEZONE);
  const endDate = addDays(startDate, 7);

  return fetchAudioData(RadioScheduleQuery, {
    stationSlug,
    startDate,
    endDate,
  }).then(({ data }) => {
    if (!data?.station.schedules) return [];
    return data.station.schedules.map((schedule) => new Schedule(schedule));
  });
}

export async function fetchStationMetadata(slug: string): Promise<Station | null> {
  return fetchAudioData(getStation, { slug }).then(({ data }) => {
    if (!data?.station) return null;
    return new Station(data.station);
  });
}
